import { rememberMediaResponse } from "../../../media/mediaReferences";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { isArray } from "lodash";
import Pagination from "@mui/material/Pagination";
import SharedCatalogueChrome from "./SharedCatalogueChrome";
import PublicCatalogueAuthGate from "./PublicCatalogueAuthGate";
import CatalogueOpenFailed from "./CatalogueOpenFailed";
import PublicCatalogueGateForm from "./PublicCatalogueGateForm";
import PublicLinkExtensionRequest from "./PublicLinkExtensionRequest";
import { useCatalogueEngagement } from "./useCatalogueEngagement";
import { viewerGateNeeded } from "../../../components/catalogue/catalogueViewerGate";
import { fetchPublicCatalogueStatus } from "../../../components/catalogue/catalogueSecurityApi";
import {
  pickPrimaryProductImage,
  gridImageUrl,
  fetchPublicCatalogueProducts,
  clearPublicCatalogueSession,
  legacyCatalogueAccessToken,
  linkTokenFromLegacySession,
  loadPublicCatalogueGuest,
  loadPublicCatalogueSession,
  savePublicCatalogueGuest,
  savePublicCatalogueSession,
  PUBLIC_CATALOGUE_PAGE_SIZE,
  pageFromSearch,
  withCataloguePage,
  focusPieceFromSearch,
  withFocusPiece,
} from "./publicCatalogueHelpers";
import { requestCatalogueAccessExtension } from "./catalogueCommerceClient";
import { isSharedCatalogueSold } from "../../products/productStockDisplay";
import {
  attachVisitorFromCatalogueSession,
  upsertVisitorShelfShortlist,
  type VisitorAuthPayload,
} from "./catalogueVisitorClient";
import { catalogueBuyerPriceCopy } from "./publicSellPrice";
import notifications from "../../../common/Components/Notifications/Notification";
import { CatalogueVisitorProvider, useCatalogueVisitor } from "./CatalogueVisitorContext";
import { rememberLastVisitedCatalogue } from "./catalogueVisitorSession";
import SharedCatalogueAuthActions from "./SharedCatalogueAuthActions";
import type { PublicCatalogueStatus } from "../../../types/catalogue.types";
import { usePublicCatalogueAnalytics } from "./usePublicCatalogueAnalytics";
import { audienceFromStatus, chromeFor, hostPhrase, parsePrice } from "./catalogueAudience";
import ResellerShareSheet, { type SharePiece } from "./ResellerShareSheet";
import ClientPieceActions from "./ClientPieceActions";
import PriceRangeFilter from "./PriceRangeFilter";
import TypeTagFilterBar, { type TypeTagOption } from "./TypeTagFilterBar";
import { toggleTypeTag, typeTagsFromSearch, withTypeTags } from "./typeTagFilter";
import { hasPriceRange, priceRangeFromSearch, withPriceRange, type PriceRange } from "./priceRange";
import CatalogueProtection from "./CatalogueProtection";
import {
  fetchClientLikes,
  fetchResellerSummary,
  openCatalogueSession,
  setClientLike,
  type ResellerSummary,
} from "../../../components/catalogue/catalogueResellerApi";

const PAGE_SIZE = PUBLIC_CATALOGUE_PAGE_SIZE;

/** identify: a gated group link waits for the viewer's name (and mobile) before it opens. */
// "failed": an open link whose open-session was refused; never the invite form.
type GateMode = "checking" | "open" | "locked" | "identify" | "failed";

const PublicCatalogueEcomerceContent = () => {
  const history = useHistory();
  const {
    isVisitorSession,
    openAuthModal,
    signOutVisitor,
    signInVisitor,
    profile,
    ensureVisitorForCommerce,
  } = useCatalogueVisitor();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const catalogueId = params.id || location?.pathname.split("/")[2];

  const searchParams = new URLSearchParams(location.search);
  const legacySessionToken = searchParams.get("session") || undefined;
  const linkToken =
    searchParams.get("link") ||
    linkTokenFromLegacySession(catalogueId, legacySessionToken) ||
    undefined;
  const accessToken = legacyCatalogueAccessToken(searchParams);
  const priceTier = searchParams.get("priceTier") || undefined;
  const priceRange = priceRangeFromSearch(location.search);
  const minPrice = priceRange.min;
  const maxPrice = priceRange.max;
  const priceFiltered = hasPriceRange(priceRange);
  const typeTagIds = typeTagsFromSearch(location.search);
  const typeTagKey = typeTagIds.join(",");
  const tagFiltered = typeTagIds.length > 0;
  const [typeTagOptions, setTypeTagOptions] = useState<TypeTagOption[]>([]);
  const [storedSessionToken, setStoredSessionToken] = useState<string | undefined>(() =>
    loadPublicCatalogueSession(catalogueId, linkToken)
  );
  const [guest, setGuest] = useState(() => loadPublicCatalogueGuest(catalogueId, linkToken));
  const sessionToken = storedSessionToken;

  const metaRef = useRef<PublicCatalogueStatus | null>(null);
  const [meta, setMeta] = useState<PublicCatalogueStatus | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const page = pageFromSearch(location.search);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [gateMode, setGateMode] = useState<GateMode>("checking");
  const [openFailReason, setOpenFailReason] = useState<string | undefined>();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestSent, setRequestSent] = useState(() =>
    Boolean(catalogueId && sessionStorage.getItem(`hkp-ext-req:${catalogueId}`) === "1")
  );
  const scrolledFocus = useRef("");
  const [trustWide, setTrustWide] = useState(true);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const loadSeq = useRef(0);
  // Reseller: pick pieces and share with their own clients.
  const [resellerSummary, setResellerSummary] = useState<ResellerSummary | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [picked, setPicked] = useState<Record<string, SharePiece>>({});
  // Reseller's client: liked pieces.
  const [likedIds, setLikedIds] = useState<Set<string>>(() => new Set());
  const openingRef = useRef(false);
  // Every piece in the active price range (all pages), from the server, for "Select all".
  const [priceMatching, setPriceMatching] = useState<{ productUniqueId: string; name?: string; price?: number }[]>([]);
  const audience = audienceFromStatus(meta);

  usePublicCatalogueAnalytics({
    catalogueId,
    linkToken,
    organizationId: meta?.organizationId,
    studioMeasurementId: meta?.googleAnalyticsMeasurementId,
  });
  // Opens and time spent, per viewer session. A studio preview is never counted.
  useCatalogueEngagement({
    catalogueId,
    linkToken,
    sessionToken,
    enabled: Boolean(meta?.engagementTracking) && !meta?.studioPreview,
  });

  useEffect(() => {
    metaRef.current = meta;
  }, [meta]);

  useEffect(() => {
    setStoredSessionToken(loadPublicCatalogueSession(catalogueId, linkToken));
    setGuest(loadPublicCatalogueGuest(catalogueId, linkToken));
  }, [catalogueId, linkToken, location.search]);

  useEffect(() => {
    if (!catalogueId || !linkToken || !sessionToken) return;
    const name = meta?.recipientName?.trim();
    if (!name) return;
    if (guest?.displayName && guest.displayName !== "Verified guest") return;
    savePublicCatalogueGuest(catalogueId, linkToken, {
      displayName: name,
      phone: guest?.phone,
    });
    setGuest(loadPublicCatalogueGuest(catalogueId, linkToken));
  }, [catalogueId, linkToken, sessionToken, meta?.recipientName, guest]);

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 721px)");
    const apply = () => setTrustWide(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const replacePageInUrl = useCallback(
    (nextPage: number) => {
      const next = withCataloguePage(new URLSearchParams(location.search), nextPage);
      const qs = next.toString();
      history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
    },
    [history, location.pathname, location.search]
  );

  const loadProducts = useCallback(
    async (pageIndex: number, opts?: { silent?: boolean; studioPreview?: boolean }) => {
      if (!catalogueId) return;
      const seq = ++loadSeq.current;
      if (!accessToken && !opts?.studioPreview && (!linkToken || !sessionToken)) {
        if (seq !== loadSeq.current) return;
        setProducts([]);
        setTotalCount(0);
        setGateMode("locked");
        return;
      }
      setLoadingProducts(true);
      // Public products API uses 0-based page index (PageRequest.of), not skip count.
      const offset = pageIndex - 1;
      try {
        const res = await fetchPublicCatalogueProducts(catalogueId, {
          offset,
          limit: PAGE_SIZE,
          sortBy: "name",
          sortOrder: "asc",
          accessToken,
          linkToken,
          sessionToken: sessionToken,
          priceTier,
          minPrice,
          maxPrice,
          typeTagIds: typeTagKey ? typeTagKey.split(",") : undefined,
        });
        if (seq !== loadSeq.current) return;
        const list = res?.data?.products || res?.data?.docs || [];
        if (res?.success && isArray(list)) {
          setGateMode("open");
          setProducts(list);
          setTotalCount(Number(res?.data?.pagination?.total ?? list.length));
          const tagOptions = res?.data?.typeTagOptions;
          setTypeTagOptions(Array.isArray(tagOptions) ? tagOptions : []);
          const matching = res?.data?.priceFilter?.matching;
          setPriceMatching(Array.isArray(matching) ? matching : []);
        } else {
          setProducts([]);
          setTotalCount(0);
          const msg = String(res?.message || "").toLowerCase();
          const accessLike =
            msg.includes("access denied") ||
            msg.includes("password") ||
            msg.includes("share link") ||
            msg.includes("otp") ||
            msg.includes("session");

          if (accessLike) {
            clearPublicCatalogueSession(catalogueId, linkToken);
            setStoredSessionToken(undefined);
            setGateMode("locked");
            if (!opts?.silent) {
              /* intentional: no toast — gate explains next step */
            }
          } else {
            setGateMode("open");
            if (!opts?.silent) {
              notifications({
                title: "Catalogue",
                message: res?.message || "Could not load products.",
                type: "danger",
              });
            }
          }
        }
      } catch {
        if (seq !== loadSeq.current) return;
        setProducts([]);
        setTotalCount(0);
        setGateMode("open");
        if (!opts?.silent) {
          notifications({
            title: "Catalogue",
            message: "Could not load products.",
            type: "danger",
          });
        }
      } finally {
        if (seq === loadSeq.current) {
          setLoadingProducts(false);
        }
      }
    },
    [catalogueId, accessToken, linkToken, sessionToken, priceTier, minPrice, maxPrice, typeTagKey]
  );

  useEffect(() => {
    if (!catalogueId) return;
    let cancelled = false;
    setLoadingMeta(true);
    (async () => {
      try {
        const json = rememberMediaResponse(await fetchPublicCatalogueStatus(catalogueId, linkToken));
        if (!cancelled && json?.success && json?.data) {
          setMeta(json.data);
        } else if (!cancelled) {
          setMeta({});
        }
      } catch {
        if (!cancelled) setMeta({});
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogueId, linkToken]);

  useEffect(() => {
    if (!catalogueId || loadingMeta || !meta) return;

    const blocked = meta.isExpired || meta.maxViewsReached;
    if (blocked) {
      setGateMode("open");
      return;
    }

    if (!accessToken && !meta.studioPreview && (!linkToken || meta.linkValid === false || !sessionToken)) {
      // OPEN sharing mode: the link opens without a code (see the effect below), unless it first
      // asks who is viewing.
      if (meta.openAccess && linkToken && meta.linkValid !== false) {
        if (viewerGateNeeded(meta.viewerGate)) {
          setProducts([]);
          setTotalCount(0);
          setGateMode("identify");
        }
        return;
      }
      setProducts([]);
      setTotalCount(0);
      setGateMode("locked");
    }
  }, [catalogueId, loadingMeta, meta, accessToken, linkToken, sessionToken]);

  useEffect(() => {
    if (!catalogueId || loadingMeta || !meta) return;
    if (meta.isExpired || meta.maxViewsReached) return;
    if (!accessToken && !meta.studioPreview && (!linkToken || meta.linkValid === false || !sessionToken)) return;

    setGateMode((current) => (current === "open" ? "open" : "checking"));
    loadProducts(page, { silent: true, studioPreview: Boolean(meta.studioPreview) });
  }, [
    catalogueId,
    loadingMeta,
    meta,
    accessToken,
    linkToken,
    sessionToken,
    priceTier,
    page,
    loadProducts,
  ]);

  useEffect(() => {
    if (loadingProducts || totalCount <= 0) return;
    const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > maxPage) {
      replacePageInUrl(maxPage);
    }
  }, [loadingProducts, totalCount, page, replacePageInUrl]);

  useEffect(() => {
    if (loadingProducts || products.length === 0) return;
    const focus = focusPieceFromSearch(location.search);
    if (!focus) return;
    const node = document.querySelector(
      `[data-hkp-piece="${CSS.escape(focus)}"]`
    ) as HTMLElement | null;
    if (!node) return;
    if (scrolledFocus.current === focus) return;
    scrolledFocus.current = focus;
    node.classList.add("is-focused");
    const id = window.requestAnimationFrame(() => {
      node.scrollIntoView({ block: "start", inline: "nearest", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [loadingProducts, products, location.search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const changePriceRange = (next: PriceRange) => {
    const qs = withPriceRange(new URLSearchParams(location.search), next).toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
  };

  const changeTypeTags = (next: string[]) => {
    const qs = withTypeTags(new URLSearchParams(location.search), next).toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
  };

  const selectAllInRange = () => {
    if (!priceMatching.length) return;
    setPickMode(true);
    setPicked((prev) => {
      const next = { ...prev };
      for (const row of priceMatching) {
        const uid = String(row.productUniqueId || "").trim();
        if (!uid || next[uid]) continue;
        const onPage = products.find((p: any) => String(p?.productUniqueId) === uid);
        next[uid] = onPage
          ? sharePiece(onPage, uid)
          : { productUniqueId: uid, name: row.name || uid, price: typeof row.price === "number" ? row.price : null, image: null };
      }
      return next;
    });
  };

  useEffect(() => {
    if (!catalogueId || !linkToken || sessionToken || !meta?.openAccess || meta.linkValid === false) return;
    // A gated link opens from the form instead.
    if (viewerGateNeeded(meta.viewerGate)) return;
    if (openingRef.current) return;
    openingRef.current = true;
    (async () => {
      try {
        const res = await openCatalogueSession(catalogueId, linkToken);
        if (res.success && res.data?.sessionToken) {
          onAuthUnlocked(res.data.sessionToken, { displayName: res.data.recipientName });
        } else {
          setOpenFailReason(res.message);
          setGateMode("failed");
        }
      } catch {
        setOpenFailReason(undefined);
        setGateMode("failed");
      } finally {
        openingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueId, linkToken, sessionToken, meta?.openAccess, meta?.linkValid, meta?.viewerGate]);

  const refreshResellerSummary = useCallback(async () => {
    if (!catalogueId || !linkToken || !sessionToken) return;
    try {
      const res = await fetchResellerSummary(catalogueId, linkToken, sessionToken);
      setResellerSummary(res.success && res.data ? res.data : null);
    } catch {
      setResellerSummary(null);
    }
  }, [catalogueId, linkToken, sessionToken]);

  const canShareWithClients = audience === "reseller" && Boolean(meta?.canShareWithClients);
  useEffect(() => {
    if (canShareWithClients && gateMode === "open") void refreshResellerSummary();
  }, [canShareWithClients, gateMode, refreshResellerSummary]);

  useEffect(() => {
    if (audience !== "client" || !catalogueId || !linkToken || !sessionToken || gateMode !== "open") return;
    let cancelled = false;
    fetchClientLikes(catalogueId, linkToken, sessionToken).then((ids) => {
      if (!cancelled) setLikedIds(new Set(ids));
    });
    return () => {
      cancelled = true;
    };
  }, [audience, catalogueId, linkToken, sessionToken, gateMode]);

  const toggleLike = async (pid: string) => {
    if (!catalogueId || !linkToken) return;
    const next = !likedIds.has(pid);
    setLikedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(pid);
      else copy.delete(pid);
      return copy;
    });
    const ok = await setClientLike(catalogueId, linkToken, sessionToken, pid, next);
    if (!ok) {
      setLikedIds((prev) => {
        const copy = new Set(prev);
        if (next) copy.delete(pid);
        else copy.add(pid);
        return copy;
      });
    }
  };

  const sharePiece = (ele: any, pid: string): SharePiece => ({
    productUniqueId: pid,
    name: typeof ele?.name === "string" ? ele.name : ele?.name?.name || "Piece",
    price: parsePrice(ele?.sellPrice),
    image: pickPrimaryProductImage(ele),
  });

  const togglePicked = (ele: any, pid: string) => {
    setPicked((prev) => {
      const copy = { ...prev };
      if (copy[pid]) delete copy[pid];
      else copy[pid] = sharePiece(ele, pid);
      return copy;
    });
  };

  const openProduct = (productUniqueId: string) => {
    const p = new URLSearchParams();
    p.set("catalogueId", catalogueId!);
    if (linkToken) p.set("link", linkToken);
    if (accessToken) p.set("token", accessToken);
    if (priceTier) p.set("priceTier", priceTier);
    // Keep the price range so "Back to collection" returns to the same filtered page.
    if (minPrice) p.set("minPrice", String(minPrice));
    if (maxPrice) p.set("maxPrice", String(maxPrice));
    if (typeTagKey) p.set("tags", typeTagKey);
    const next = withFocusPiece(withCataloguePage(p, page), productUniqueId);
    history.push(
      `/catalogue-product-info/${encodeURIComponent(productUniqueId)}?${next.toString()}`
    );
  };

  const addPieceToSelection = async (ele: any, pid: string) => {
    if (!catalogueId || selectingId || isSharedCatalogueSold(ele)) return;
    const ready =
      isVisitorSession ||
      (await ensureVisitorForCommerce({
        sessionToken,
      }));
    if (!ready) {
      openAuthModal();
      return;
    }
    setSelectingId(pid);
    try {
      const name =
        typeof ele?.name === "string" ? ele.name : ele?.name?.name || "Product";
      const shelfRes = await upsertVisitorShelfShortlist(catalogueId, {
        productKey: pid,
        productId: pid,
        productUniqueId: pid,
        productName: name,
        productColor: ele?.color,
        productImage: pickPrimaryProductImage(ele) || undefined,
        designNum: ele?.designNum,
        shareLinkToken: linkToken,
      });
      if (!shelfRes?.success) {
        notifications({
          title: "Selection",
          message: shelfRes?.message || "Could not save this piece.",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Selection",
        message: "Added to My selections. Submit from there when you are ready.",
        type: "success",
      });
    } catch {
      notifications({
        title: "Selection",
        message: "Network error. Try again.",
        type: "danger",
      });
    } finally {
      setSelectingId(null);
    }
  };

  const handlePageChange = (_: any, value: number) => {
    if (value === page) return;
    replacePageInUrl(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onAuthUnlocked = (
    newSession: string,
    guestInfo?: { displayName?: string; phone?: string },
    visitor?: VisitorAuthPayload
  ) => {
    if (catalogueId && linkToken) {
      savePublicCatalogueSession(catalogueId, linkToken, newSession);
      savePublicCatalogueGuest(catalogueId, linkToken, {
        displayName:
          guestInfo?.displayName ||
          metaRef.current?.recipientName ||
          "Verified guest",
        phone: guestInfo?.phone,
      });
      setGuest(loadPublicCatalogueGuest(catalogueId, linkToken));
    }
    setStoredSessionToken(newSession);
    const p = new URLSearchParams(location.search);
    p.delete("session");
    const nextSearch = p.toString();
    history.replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`);
    if (visitor?.token) {
      signInVisitor(visitor.token, visitor.profile);
      rememberLastVisitedCatalogue(catalogueId || "", nextSearch);
    }
  };

  const openSavedSelections = () => {
    rememberLastVisitedCatalogue(
      catalogueId || "",
      location.search.replace(/^\?/, "")
    );
    history.push("/catalogue-visitor/account");
  };

  const onOpenAccount = async () => {
    if (isVisitorSession) {
      openSavedSelections();
      return;
    }
    if (sessionToken && catalogueId) {
      const payload = await attachVisitorFromCatalogueSession(catalogueId, sessionToken);
      if (payload) {
        signInVisitor(payload.token, payload.profile);
        openSavedSelections();
        return;
      }
    }
    openAuthModal();
  };

  const leaveCollection = () => {
    clearPublicCatalogueSession(catalogueId, linkToken);
    setStoredSessionToken(undefined);
    setGuest(undefined);
  };

  const chrome = chromeFor(audience, meta, loadingMeta);
  const title =
    meta?.catalogueName || (loadingMeta ? "Loading…" : "Collection");
  const description =
    meta?.catalogueDescription ||
    "Each piece in this collection can be explored for authenticity and traceability through HastKala Pramanak.";

  const blocked =
    meta &&
    (meta.isExpired ||
      meta.maxViewsReached ||
      (Boolean(linkToken) && meta.linkTokenProvided === true && meta.linkValid === false));

  const expiryMsFromMeta = (() => {
    if (Boolean(linkToken) && meta?.linkTokenProvided && meta?.linkValid === false) {
      return null;
    }
    if (meta?.linkExpiresAt) {
      const ts = new Date(meta.linkExpiresAt).getTime();
      return Number.isFinite(ts) ? ts : null;
    }
    if (typeof meta?.remainingTime === "number" && meta.remainingTime > 0) {
      return Date.now() + meta.remainingTime;
    }
    return null;
  })();
  const remainingMs =
    expiryMsFromMeta != null ? Math.max(0, expiryMsFromMeta - nowMs) : null;
  const isAboutToExpire = remainingMs != null && remainingMs > 0 && remainingMs <= 5 * 60 * 60 * 1000;
  const formatCountdownCompact = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    let rem = totalSec;
    const days = Math.floor(rem / (24 * 3600));
    rem -= days * 24 * 3600;
    const hours = Math.floor(rem / 3600);
    rem -= hours * 3600;
    const mins = Math.floor(rem / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    const secs = rem - mins * 60;
    return `${mins}m ${secs}s`;
  };

  // A reseller's client asks the reseller, never the studio.
  const canRequestExtension =
    audience !== "client" && Boolean((isVisitorSession && profile?.visitorId) || guest?.phone);

  const handleRequestExtension = async () => {
    if (!catalogueId || requestBusy || requestSent) return;
    if (!canRequestExtension) {
      notifications({
        title: "Sign in required",
        message: "Please sign in first so the studio can identify your extension request.",
        type: "warning",
      });
      openAuthModal();
      return;
    }
    try {
      setRequestBusy(true);
      const res = await requestCatalogueAccessExtension({
        catalogueId,
        clientName: profile?.name || guest?.displayName || meta?.recipientName || undefined,
        clientContact: profile?.phone || guest?.phone || undefined,
        clientEmail: profile?.email || undefined,
        clientMessage: "Please extend access to this shared catalogue.",
        linkToken,
      });
      if (res?.success) {
        sessionStorage.setItem(`hkp-ext-req:${catalogueId}`, "1");
        setRequestSent(true);
        notifications({
          title: "Sent",
          message: "Extension request sent to the studio.",
          type: "success",
        });
      } else {
        notifications({
          title: "Could not send",
          message: res?.message || "Please try again.",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Could not send",
        message: "Network error. Try again.",
        type: "danger",
      });
    } finally {
      setRequestBusy(false);
    }
  };

  const showMainContent = !blocked && gateMode === "open";
  // An open link has no invite password; if it ever lands on "locked", it failed to open.
  const showGate = !blocked && gateMode === "locked" && !meta?.openAccess;
  const showOpenFailed =
    !blocked && (gateMode === "failed" || (gateMode === "locked" && Boolean(meta?.openAccess)));
  const showIdentify = !blocked && gateMode === "identify" && !sessionToken;

  useEffect(() => {
    if (catalogueId && showMainContent) {
      rememberLastVisitedCatalogue(catalogueId, location.search.replace(/^\?/, ""));
    }
  }, [catalogueId, showMainContent, location.search]);

  const visitorHeaderActions = (
    <SharedCatalogueAuthActions
      // A reseller's client sees their own invite name, never a studio visitor account on this browser.
      isVisitorSession={audience === "client" ? false : isVisitorSession}
      visitorName={profile?.name}
      guestName={guest?.displayName || meta?.recipientName}
      catalogueUnlocked={Boolean(sessionToken)}
      onMySelections={() => openSavedSelections()}
      onOpenAuth={() => {
        void onOpenAccount();
      }}
      onSignOutVisitor={() => signOutVisitor()}
      onLeaveCollection={leaveCollection}
      hideAccount={audience === "client"}
    />
  );

  const trustNotes = (
    <section className="hkp-shared-trust" aria-label="Trust">
      <div className="hkp-shared-trust-item">
        <div className="hkp-shared-trust-icon" aria-hidden="true">
          ◎
        </div>
        <div>
          <h3>{audience === "client" ? "Recorded at the loom" : "Certificate-linked"}</h3>
          <p>
            {audience === "client"
              ? "Each piece's making story was recorded as it was woven."
              : "Verification and certification details appear on each product where available."}
          </p>
        </div>
      </div>
      <div className="hkp-shared-trust-item">
        <div className="hkp-shared-trust-icon" aria-hidden="true">
          ◇
        </div>
        <div>
          <h3>Traceable craft</h3>
          <p>Artisan, loom, and origin context are shown on the product page.</p>
        </div>
      </div>
      <div className="hkp-shared-trust-item">
        <div className="hkp-shared-trust-icon" aria-hidden="true">
          ✓
        </div>
        <div>
          <h3>{audience === "client" ? "Shared with you" : "From your host"}</h3>
          <p>
            {audience === "client"
              ? `Picked for you by ${hostPhrase(audience, meta)}.`
              : "This page was shared by the maker or partner you are working with."}
          </p>
        </div>
      </div>
    </section>
  );

  const expiryActionLabel = requestBusy ? "Sending…" : requestSent ? "Sent" : "Extend";
  const expiryCard =
    remainingMs != null ? (
      <aside
        className={`hkp-shared-expiry-card${isAboutToExpire ? " is-urgent" : ""}`}
        role="status"
      >
        {remainingMs > 0 ? (
          <>
            <p className="hkp-shared-expiry-label">
              {isAboutToExpire ? "Ending soon" : "Access"}
            </p>
            <p className="hkp-shared-expiry-time">{formatCountdownCompact(remainingMs)}</p>
            {canRequestExtension ? (
              <button
                type="button"
                className={`hkp-shared-expiry-action${requestSent ? " is-sent" : ""}`}
                onClick={handleRequestExtension}
                disabled={requestBusy || requestSent}
              >
                {expiryActionLabel}
              </button>
            ) : null}
          </>
        ) : (
          <>
            <p className="hkp-shared-expiry-label">Link expired</p>
            {canRequestExtension ? (
              <button
                type="button"
                className={`hkp-shared-expiry-action${requestSent ? " is-sent" : ""}`}
                onClick={handleRequestExtension}
                disabled={requestBusy || requestSent}
              >
                {expiryActionLabel}
              </button>
            ) : null}
          </>
        )}
      </aside>
    ) : null;

  const pickedList = Object.values(picked);
  const remainingShares = resellerSummary?.forwardsRemaining ?? null;
  const resellerBar = (
    <div className="hkp-rs-bar" role="region" aria-label="Share with my client">
      <div className="hkp-rs-bar-inner">
        <div className="hkp-rs-bar-text">
          <span className="hkp-rs-bar-title">
            {pickMode
              ? pickedList.length
                ? `${pickedList.length} piece${pickedList.length === 1 ? "" : "s"} selected`
                : "Tap pieces to select"
              : "Client shares"}
          </span>
          {resellerSummary ? (
            <span className={`hkp-rs-bar-sub${remainingShares === 0 ? " is-empty" : ""}`}>
              {resellerSummary.forwardsRemaining > 0
                ? `${resellerSummary.forwardsRemaining} of ${resellerSummary.forwardLimit} left`
                : "All used"}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="hkp-rs-select-toggle"
          aria-pressed={pickMode}
          onClick={() => {
            if (pickMode) setPicked({});
            setPickMode((v) => !v);
          }}
        >
          {pickMode ? "Cancel" : "Select pieces"}
        </button>
        <button type="button" className="hkp-rs-bar-btn" onClick={() => setShareOpen(true)}>
          {pickMode && pickedList.length ? `Share ${pickedList.length}` : "Share"}
        </button>
      </div>
    </div>
  );

  return (
    <>
    <SharedCatalogueChrome
      organizationName={chrome.hostName}
      subtitle={chrome.subtitle}
      pillText={chrome.pill}
      mark={chrome.mark}
      footerText={audience === "client" ? chrome.footer : undefined}
      headerActions={visitorHeaderActions}
      bottomBar={showMainContent && canShareWithClients ? resellerBar : undefined}
    >
      {blocked && catalogueId && linkToken && meta?.linkValid === false && meta?.extensionRequestable ? (
        <PublicLinkExtensionRequest
          catalogueId={catalogueId}
          linkToken={linkToken}
          hostLabel={hostPhrase(audience, meta)}
          needsName={Boolean(meta.extensionNeedsName)}
          alreadyRequested={Boolean(meta.extensionRequested)}
          declined={Boolean(meta.extensionDeclined)}
        />
      ) : blocked && (
        <div className="hkp-shared-muted" role="alert">
          {Boolean(linkToken) && meta?.linkTokenProvided && meta?.linkValid === false
            ? `This invite link has expired or been disabled. Ask ${hostPhrase(audience, meta)} to send a new link.`
            : meta?.isExpired
            ? "This catalogue is no longer available."
            : "This catalogue has reached its view limit."}
        </div>
      )}

      {showIdentify && catalogueId && linkToken && meta?.viewerGate && (
        <>
          <div className="hkp-shared-main-top">
            <section className="hkp-shared-hero">
              <h1>{title}</h1>
              <p>{description}</p>
            </section>
            {expiryCard}
          </div>
          <PublicCatalogueGateForm
            catalogueId={catalogueId}
            linkToken={linkToken}
            gate={meta.viewerGate}
            catalogueTitle={meta?.catalogueName}
            hostLabel={hostPhrase(audience, meta)}
            onUnlocked={onAuthUnlocked}
          />
        </>
      )}

      {showOpenFailed && (
        <>
          <div className="hkp-shared-main-top">
            <section className="hkp-shared-hero">
              <h1>{title}</h1>
              <p>{description}</p>
            </section>
            {expiryCard}
          </div>
          <CatalogueOpenFailed reason={openFailReason} />
        </>
      )}

      {showGate && catalogueId && (
        <>
          <div className="hkp-shared-main-top">
            <section className="hkp-shared-hero">
              <h1>{title}</h1>
              <p>{description}</p>
            </section>
            {expiryCard}
          </div>
          <PublicCatalogueAuthGate
            catalogueId={catalogueId}
            catalogueTitle={meta?.catalogueName}
            unlockStatus={meta}
            onUnlocked={onAuthUnlocked}
            hostLabel={hostPhrase(audience, meta)}
          />
        </>
      )}

      {showMainContent && (
        <>
          <div className="hkp-shared-main-top">
            <section className="hkp-shared-hero">
              <h1>{title}</h1>
              <p>{description}</p>
            </section>
            {expiryCard}
          </div>

          <div className="hkp-shared-section-head">
            <h2>Pieces in this collection</h2>
            <span>
              {loadingProducts && products.length === 0
                ? "Loading…"
                : `${totalCount} ${priceFiltered ? "in range" : `item${totalCount === 1 ? "" : "s"}`}${tagFiltered ? " in this type" : ""}`}
            </span>
          </div>

          <TypeTagFilterBar
            options={typeTagOptions}
            selected={typeTagIds}
            onToggle={(id) => changeTypeTags(toggleTypeTag(typeTagIds, id))}
            onClear={() => changeTypeTags([])}
          />

          {totalCount > 0 || priceFiltered ? (
            <div className="hkp-price-filter-bar">
              <PriceRangeFilter
                value={priceRange}
                onChange={changePriceRange}
                samplePrices={products.map((p: any) => parsePrice(p?.sellPrice)).filter((n: number | null): n is number => n != null)}
              />
              {canShareWithClients && priceFiltered && priceMatching.length > 0 ? (
                <button
                  type="button"
                  className="hkp-price-select-all"
                  onClick={selectAllInRange}
                  disabled={priceMatching.every((row) => Boolean(picked[row.productUniqueId]))}
                >
                  {priceMatching.every((row) => Boolean(picked[row.productUniqueId]))
                    ? `All ${priceMatching.length} selected`
                    : `Select all ${priceMatching.length} in this range`}
                </button>
              ) : null}
            </div>
          ) : null}

          {loadingProducts && products.length === 0 ? (
            <div className="hkp-shared-muted">Loading products…</div>
          ) : (
            <>
              {products.length === 0 ? (
                <div className="hkp-shared-muted">
                  {tagFiltered && totalCount === 0 ? (
                    <>
                      No pieces of this type.{" "}
                      <button type="button" className="hkp-price-clear" onClick={() => changeTypeTags([])}>
                        Show all pieces
                      </button>
                    </>
                  ) : priceFiltered && totalCount === 0 ? (
                    <>
                      No pieces in this price range.{" "}
                      <button type="button" className="hkp-price-clear" onClick={() => changePriceRange({})}>
                        Show all pieces
                      </button>
                    </>
                  ) : totalCount > 0
                    ? "No products on this page. Try another page."
                    : "No products in this catalogue yet."}
                </div>
              ) : (
                <div className="hkp-shared-grid">
                  {products
                    .filter((ele: any) => String(ele?.productUniqueId || "").trim())
                    .map((ele: any, index: number) => {
                    const pid = String(ele.productUniqueId).trim();
                    const img = gridImageUrl(pickPrimaryProductImage(ele));
                    const name =
                      typeof ele?.name === "string"
                        ? ele.name
                        : ele?.name?.name || "Product";
                    const priceCopy = catalogueBuyerPriceCopy(ele?.sellPrice, ele?.gstRate);
                    const sold = isSharedCatalogueSold(ele);
                    const picking = pickMode && canShareWithClients;
                    const isPicked = Boolean(picked[pid]);
                    return (
                      <article
                        key={pid}
                        className={`hkp-shared-card${audience === "client" ? " is-client" : ""}${picking ? " is-pickable" : ""}${picking && isPicked ? " is-picked" : ""}`}
                        data-hkp-piece={pid}
                      >
                        <button
                          type="button"
                          className="hkp-shared-card-open"
                          aria-pressed={picking ? isPicked : undefined}
                          onClick={() => (picking ? togglePicked(ele, pid) : openProduct(pid))}
                        >
                          <div className="hkp-shared-card-visual">
                            {img ? (
                              // Only tiles near the screen load; the first row loads at once.
                              <img
                                className="hkp-shared-card-img"
                                src={img}
                                alt=""
                                loading={index < 4 ? "eager" : "lazy"}
                                decoding="async"
                                onLoad={(e) => e.currentTarget.classList.add("is-loaded")}
                              />
                            ) : null}
                            <span className="hkp-shared-card-badge">HKP</span>
                            {sold ? (
                              <span className="hkp-shared-card-badge hkp-shared-card-badge-sold">
                                Sold
                              </span>
                            ) : null}
                          </div>
                          <div className="hkp-shared-card-body">
                            <p className="hkp-shared-card-title">{name}</p>
                            <p className="hkp-shared-card-meta">
                              {ele?.category?.name ||
                                ele?.artform?.name ||
                                "Handloom"}
                              {ele?.srNo && audience !== "client" ? ` · Sr. ${ele.srNo}` : ""}
                            </p>
                            <div className="hkp-shared-card-row">
                              <span className="hkp-shared-price">
                                {priceCopy ? priceCopy.amount : audience === "client" ? "Ask for price" : "Price on request"}
                              </span>
                              <span className="hkp-shared-link">{picking ? (isPicked ? "Selected" : "Tap to select") : "View detail →"}</span>
                            </div>
                            {priceCopy ? (
                              <p className="hkp-shared-price-gst">
                                {priceCopy.label} {priceCopy.detail}
                              </p>
                            ) : null}
                          </div>
                        </button>
                        {audience === "client" ? (
                          <ClientPieceActions
                            pieceId={pid}
                            pieceName={name}
                            liked={likedIds.has(pid)}
                            onToggleLike={() => void toggleLike(pid)}
                            whatsApp={meta?.curatorWhatsApp}
                            curatorName={meta?.curatorName}
                            catalogueId={catalogueId}
                            linkToken={linkToken}
                            sessionToken={sessionToken}
                          />
                        ) : (
                          <div className="hkp-shared-card-actions">
                            <button
                              type="button"
                              className="hkp-shared-card-select"
                              disabled={sold || selectingId === pid}
                              onClick={() => void addPieceToSelection(ele, pid)}
                            >
                              {selectingId === pid ? "Adding…" : "Add to selection"}
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
              {totalPages > 1 && (
                <div className="hkp-shared-pagination">
                  <Pagination
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                  />
                </div>
              )}
            </>
          )}

          {trustWide ? (
            trustNotes
          ) : (
            <details className="hkp-shared-trust-details">
              <summary className="hkp-shared-trust-summary">
                Why this collection is trusted
              </summary>
              {trustNotes}
            </details>
          )}
        </>
      )}

      {!blocked && gateMode === "checking" && !showGate && !showIdentify && !showOpenFailed && (
        <div className="hkp-shared-muted">Checking access…</div>
      )}
    </SharedCatalogueChrome>
    {showMainContent && meta ? (
      <CatalogueProtection
        status={meta}
        viewerName={profile?.name || guest?.displayName || meta.recipientName}
      />
    ) : null}
    {canShareWithClients && catalogueId && linkToken ? (
      <ResellerShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        catalogueId={catalogueId}
        linkToken={linkToken}
        sessionToken={sessionToken}
        catalogueName={meta?.catalogueName}
        totalPieces={totalCount}
        selected={pickedList}
        samplePiece={(() => {
          const first = products.find((p: any) => parsePrice(p?.sellPrice) && p?.productUniqueId);
          return first ? sharePiece(first, String(first.productUniqueId)) : null;
        })()}
        summary={resellerSummary}
        onSummaryChange={setResellerSummary}
        onRefreshSummary={() => void refreshResellerSummary()}
      />
    ) : null}
    </>
  );
};

const PublicCatalogueEcomerce = () => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const catalogueId =
    params.id?.trim() || location.pathname.split("/").filter(Boolean).pop() || undefined;

  return (
    <CatalogueVisitorProvider catalogueId={catalogueId}>
      <PublicCatalogueEcomerceContent />
    </CatalogueVisitorProvider>
  );
};

export default PublicCatalogueEcomerce;

